import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, FileText, FileDown, Save, Link2, Edit3, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const btn = "inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-40";

async function download(jobId, fmt) {
  try {
    const r = await api.get(`/jobs/${jobId}/cover-letter.${fmt}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    const m = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "");
    a.href = url; a.download = m ? m[1] : `Anschreiben.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { err(e); }
}

function CoverLetter({ job, onSaved }) {
  const [posting, setPosting] = useState(job.job_posting || "");
  const [text, setText] = useState(job.cover_letter || "");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { err(e); } finally { setBusy(""); } };
  const fetchPosting = () => run("fetch", async () => {
    const r = await api.post(`/jobs/${job.job_id}/fetch-posting`);
    setPosting(r.data.job_posting); toast.success("Stellenanzeige geladen");
  });
  const draft = (refine) => run(refine ? "refine" : "draft", async () => {
    const r = await api.post(`/jobs/${job.job_id}/cover-letter/draft`, { job_posting: posting, refine_hint: refine ? hint : null });
    setText(r.data.cover_letter); setDirty(false); setHint(""); toast.success("Anschreiben erstellt"); onSaved();
  });
  const save = () => run("save", async () => {
    await api.patch(`/jobs/${job.job_id}`, { cover_letter: text, job_posting: posting });
    setDirty(false); toast.success("Anschreiben gespeichert"); onSaved();
  });
  const dl = (fmt) => run(fmt, async () => { if (dirty) await api.patch(`/jobs/${job.job_id}`, { cover_letter: text }); setDirty(false); await download(job.job_id, fmt); });
  return (
    <div className="space-y-3 border-t border-slate-200 pt-4" data-testid="cover-letter-section">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Anschreiben</div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-600">Stellenanzeige (Text)</span>
          {job.link && <button onClick={fetchPosting} disabled={!!busy} className="text-xs text-[#009F9C] hover:underline inline-flex items-center gap-1" data-testid="cover-fetch-posting">{busy === "fetch" ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />} Aus Link laden</button>}
        </div>
        <textarea value={posting} onChange={(e) => setPosting(e.target.value)} rows={4} placeholder="Anzeigentext hier einfügen – oder über „Aus Link laden“ automatisch auslesen" className="w-full border border-slate-300 px-3 py-2 text-xs focus:outline-none focus:border-slate-900" data-testid="cover-posting-input" />
      </div>
      <button onClick={() => draft(false)} disabled={!!busy} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-black disabled:opacity-50" data-testid="cover-draft-btn">
        {busy === "draft" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {text ? "Neuen KI-Vorschlag erstellen" : "KI-Vorschlag erstellen"}
      </button>
      {busy === "draft" && <div className="text-xs text-slate-500">Die KI schreibt – das dauert ca. 20 Sekunden …</div>}
      {text && (
        <>
          <textarea value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }} rows={14} className="w-full border border-slate-300 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-slate-900" data-testid="cover-text-input" />
          <div className="flex gap-2">
            <input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Überarbeiten, z. B. „kürzer“, „formeller“, „Projekt X erwähnen“" className="flex-1 border border-slate-300 px-3 py-1.5 text-xs focus:outline-none focus:border-slate-900" data-testid="cover-refine-input" />
            <button onClick={() => draft(true)} disabled={!!busy || !hint.trim()} className={btn} data-testid="cover-refine-btn">{busy === "refine" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Überarbeiten</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={!!busy || !dirty} className={btn} data-testid="cover-save-btn"><Save size={12} /> Speichern</button>
            <button onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Kopiert"))} className={btn} data-testid="cover-copy-btn"><Copy size={12} /> Kopieren</button>
            <button onClick={() => dl("pdf")} disabled={!!busy} className={btn} data-testid="cover-pdf-btn"><FileDown size={12} /> PDF</button>
            <button onClick={() => dl("docx")} disabled={!!busy} className={btn} data-testid="cover-docx-btn"><FileText size={12} /> Word</button>
          </div>
        </>
      )}
    </div>
  );
}

export function JobDetailDialog({ job, statusMeta, onClose, onEdit, onDelete, onSaved }) {
  const st = statusMeta[job.status] || statusMeta.none;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">{job.function}</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">{job.company}{job.contact ? ` · ${job.contact}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className={`px-2 py-0.5 border ${st.cls}`}>{st.label}</span>
          <span className="text-slate-500">Gesendet: {job.applied_on ? formatDeShort(job.applied_on) : "—"}</span>
          {job.link && <a href={job.link.startsWith("http") ? job.link : `https://${job.link}`} target="_blank" rel="noreferrer" className="text-[#009F9C] hover:underline inline-flex items-center gap-1">Stellenangebot <ExternalLink size={11} /></a>}
          <span className="flex-1" />
          <button onClick={onEdit} className={btn} data-testid="job-detail-edit"><Edit3 size={12} /> Bearbeiten</button>
          <button onClick={onDelete} className={`${btn} text-red-700 hover:!bg-red-600`} data-testid="job-detail-delete"><Trash2 size={12} /> Löschen</button>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Notizen</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 p-3 min-h-[48px]" data-testid="job-detail-notes">{job.notes || "Keine Notizen."}</div>
        </div>
        <CoverLetter job={job} onSaved={onSaved} />
      </DialogContent>
    </Dialog>
  );
}
